import React from 'react';
import { FooterSection } from '../components/landing';
import { usePageTitle } from '../hooks/usePageTitle';

const PrivacyPolicyPage: React.FC = () => {
  usePageTitle('Privacy Policy');

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-orange-800 to-slate-900 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-semibold mb-6 backdrop-blur-sm border border-white/20">
              Legal
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-300">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: February 11, 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none text-gray-600">

            {/* 1. Introduction */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p>
                Retrosive ("we", "our", or "us") operates the website located at{' '}
                <strong>retrosive.com</strong> and provides the Retrosive platform (the "Service").
                This Privacy Policy describes how we collect, use, disclose, and safeguard your
                information when you use our Service.
              </p>
              <p className="mt-4">
                By accessing or using our Service, you agree to the collection and use of
                information in accordance with this Privacy Policy. If you do not agree with
                the terms of this Privacy Policy, please do not access the Service.
              </p>
            </div>

            {/* 2. Information We Collect */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.1 Information You Provide</h3>
              <p>We collect information that you voluntarily provide when you:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Register for an account (email address, first name, last name, password)</li>
                <li>Set up your profile (display name, avatar image)</li>
                <li>Create or join organizations and teams</li>
                <li>Create boards and add content (cards, action items, votes, reactions)</li>
                <li>Subscribe to a paid plan (billing information processed by Stripe)</li>
                <li>Contact us for support</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.2 Information Collected Automatically</h3>
              <p>When you use our Service, we may automatically collect:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>IP address</li>
                <li>Browser type and version (User-Agent)</li>
                <li>Device information</li>
                <li>Pages visited and features used</li>
                <li>Timestamps and session data</li>
                <li>Request identifiers for troubleshooting purposes</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.3 Information from Third-Party Services</h3>
              <p>
                If you choose to sign in using Google OAuth, we receive basic profile information
                (name and email address) from Google. We do not receive or store your Google password.
              </p>
            </div>

            {/* 3. How We Use Your Information */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Provide, operate, and maintain the Service</li>
                <li>Create and manage your account</li>
                <li>Process transactions and manage subscriptions</li>
                <li>Enable real-time collaboration features (WebSocket connections)</li>
                <li>Send transactional emails (account verification, password resets, billing notifications)</li>
                <li>Monitor and analyze usage patterns to improve the Service</li>
                <li>Detect, prevent, and address technical issues or security threats</li>
                <li>Enforce rate limiting and prevent abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            {/* 4. Data Sharing */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
              <p>
                We do not sell your personal information. We may share your information only in
                the following circumstances:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>
                  <strong>With your team members:</strong> Board content, cards, votes, and action items
                  are visible to team and organization members as part of the collaborative nature of the Service.
                </li>
                <li>
                  <strong>Service providers:</strong> We use third-party services to help us operate
                  the Service:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Paddle</strong> - for payment processing</li>
                    <li><strong>Cloudflare</strong> - for CDN, security, and DDoS protection</li>
                    <li><strong>Google</strong> - for OAuth authentication (optional)</li>
                  </ul>
                </li>
                <li>
                  <strong>Legal requirements:</strong> If required by law, regulation, legal process,
                  or governmental request.
                </li>
                <li>
                  <strong>Protection of rights:</strong> To protect the rights, property, or safety
                  of Retrosive, our users, or the public.
                </li>
              </ul>
            </div>

            {/* 5. Data Security */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your
                personal information, including:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>All data transmitted over HTTPS/TLS encryption</li>
                <li>Passwords are hashed and never stored in plain text</li>
                <li>JWT-based authentication with token expiration and blacklisting</li>
                <li>Rate limiting to prevent brute-force attacks</li>
                <li>CORS and CSRF protection</li>
                <li>Regular security monitoring and audit logging</li>
              </ul>
              <p className="mt-4">
                While we strive to protect your information, no method of transmission over the
                Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </div>

            {/* 6. Cookies */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookies and Local Storage</h2>
              <p>We use the following types of cookies and browser storage:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>
                  <strong>Essential cookies:</strong> Required for the Service to function properly
                  (e.g., authentication tokens, session management).
                </li>
                <li>
                  <strong>Local storage:</strong> Used to store authentication tokens and user
                  preferences for a seamless experience.
                </li>
              </ul>
              <p className="mt-4">
                We do not use advertising or third-party tracking cookies.
              </p>
            </div>

            {/* 7. Data Retention */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p>
                We retain your personal information for as long as your account is active or as
                needed to provide you with the Service. Board data may be automatically deleted
                after extended periods of inactivity, as indicated within the Service.
              </p>
              <p className="mt-4">
                When you delete your account, we will delete or anonymize your personal information
                within a reasonable timeframe, except where we are required to retain certain
                information for legal or legitimate business purposes.
              </p>
            </div>

            {/* 8. Your Rights */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights</h2>
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to or restrict the processing of your data</li>
                <li>Request data portability</li>
                <li>Withdraw consent at any time (where consent is the basis for processing)</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:admin@retrosive.com" className="text-orange-500 hover:text-orange-600 font-medium">
                  admin@retrosive.com
                </a>.
              </p>
            </div>

            {/* 9. Children's Privacy */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p>
                Our Service is not intended for individuals under the age of 16. We do not knowingly
                collect personal information from children under 16. If we learn that we have
                collected personal information from a child under 16, we will take steps to delete
                that information promptly.
              </p>
            </div>

            {/* 10. International Data Transfers */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than the
                country in which you reside. These countries may have data protection laws that
                are different from the laws of your country. By using the Service, you consent
                to the transfer of your information to these countries.
              </p>
            </div>

            {/* 11. Changes */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on this page and updating the "Last updated"
                date. Your continued use of the Service after any changes constitutes your acceptance
                of the updated Privacy Policy.
              </p>
            </div>

            {/* 12. Contact */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our data practices,
                please contact us at:
              </p>
              <div className="mt-4 p-6 bg-gray-50 rounded-xl border border-gray-200">
                <p className="font-semibold text-gray-900">Retrosive</p>
                <p className="mt-2">
                  Email:{' '}
                  <a href="mailto:admin@retrosive.com" className="text-orange-500 hover:text-orange-600 font-medium">
                    admin@retrosive.com
                  </a>
                </p>
                <p className="mt-1">
                  Website:{' '}
                  <a href="https://retrosive.com" className="text-orange-500 hover:text-orange-600 font-medium">
                    retrosive.com
                  </a>
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default PrivacyPolicyPage;
